import React from 'react';
import { Checkbox } from '../../../../../components/Checkbox';
import { DropdownSearchSelect, DropdownSelect } from '../../../../../components/Dropdown';
import { TextInput } from '../../../../../components/TextInput';
import { PdfDataKey } from '../../../data/PdfDataKey';
import { PdfFieldResolving } from '../../../data/PdfFieldResolving';
import { PdfSortDirection, PdfSortType, PdfTableComponent, PdfTableSorting, PdfTableType } from '../../../PdfComponent';
import { usePdfTemplateContext } from '../../../PdfTemplateEditorContext';

export default function PdfTemplateTableRowsSettings<T>({ component }: { component: PdfTableComponent }) {
  const { updateComponent, tableProviders, data } = usePdfTemplateContext<T>();
  const tableDataProvider = tableProviders.find(tp => tp.tableType === component.tableType);
  if (!tableDataProvider) return null;

  const sorting: PdfTableSorting = component.sorting?.[0];

  return (
    <>
      <TextInput
        type='number'
        label={'Row height (mm)'}
        value={component.rowHeight}
        onChange={v => updateComponent(component.id, { rowHeight: Number(v) })}
      />
      <Checkbox
        label={'Sorting'}
        value={!!sorting}
        onChange={checked => {
          updateComponent(component.id, {
            sorting: checked
              ? [
                  {
                    key: component.columns[0].key!,
                    direction: PdfSortDirection.ascending,
                    sortType: PdfSortType.string,
                  },
                ]
              : [],
          });
        }}
      />
      {component.sorting?.length ? (
        <>
          <div className='grid grid-cols-3 gap-2'>
            <div className='col-span-3'>
              <DropdownSearchSelect<PdfDataKey<any>>
                placeholder={'Select a sort key'}
                label={'Sort key'}
                items={tableDataProvider.columns}
                textBuilder={key => PdfFieldResolving.getLabel(key, data)}
                onSelect={key =>
                  updateComponent(component.id, {
                    sorting: [{ ...sorting, key }],
                  })
                }
                value={sorting.key}
              />
            </div>
          </div>
          <div className='flex gap-2'>
            <DropdownSelect<PdfSortDirection>
              label={'Direction'}
              items={[PdfSortDirection.ascending, PdfSortDirection.descending]}
              textBuilder={dir => (dir === PdfSortDirection.ascending ? 'Ascending' : 'Descending')}
              onSelect={dir => updateComponent(component.id, { sorting: [{ ...sorting, direction: dir }] })}
              value={sorting.direction}
            />
            <DropdownSelect<PdfSortType>
              textBuilder={t => t}
              label={'Type'}
              items={Object.values(PdfSortType)}
              onSelect={sortType => updateComponent(component.id, { sorting: [{ ...sorting, sortType }] })}
              value={sorting.sortType}
            />
          </div>
        </>
      ) : null}
      <DropdownSearchSelect
        placeholder={'Select a group key (optional)'}
        label={'Group by (optional)'}
        items={tableDataProvider.columns}
        textBuilder={key => PdfFieldResolving.getLabel(key, data)}
        onSelect={key => updateComponent(component.id, { groupKey: key })}
        value={component.groupKey}
      />
      {component.tableType === PdfTableType.productTransaction && (
        <Checkbox
          label={'Completed only'}
          tooltip={'Only show product transactions that are in their finished state'}
          value={component.settings?.productTransactions?.completedOnly ?? false}
          onChange={checked =>
            updateComponent(component.id, {
              settings: {
                ...component.settings,
                productTransactions: { ...component.settings?.productTransactions, completedOnly: checked },
              },
            })
          }
        />
      )}
    </>
  );
}
